import React, { useRef, useState, useMemo } from "react";
import { useGosvaraSheet } from "../../hooks/useGosvaraSheet";
import { useSchools } from "../../hooks/useSchools";
import { useSessionClass } from "../../contexts/SessionClassContext";
import { useSubjectsByClass, useSubjectsByClassId } from "../../hooks/useSubjects";
import {
  Box,
  CircularProgress,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  Button,
} from "@mui/material";
import SchoolIcon from "@mui/icons-material/School";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ClassIcon from "@mui/icons-material/Class";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { Download } from "@mui/icons-material";
import html2canvas from "html2canvas";
import { useTranslation } from "react-i18next";

interface Props {
  schoolId: number;
  classId: number;
  marksheetType: string;
  monthType: string;
  gosvaraData?: any; // Optional prop to pass the actual gosvara data
}

const defaultGradeHeaders = ["A+", "A", "B", "C", "D", "E1", "E2"];
const defaultGradeRanges = [
  "(85 - 100)",
  "(75 - 84)",
  "(60 - 74)",
  "(45 - 59)",
  "(33 - 44)",
  "(20 - 32)",
  "(0 - 19)",
];

const sectionColor = "#e6e6fa";
const headerColor = "#d6c1e6";
const arrowColor = "#2ecc40";
const hindiHeaderColor = "#ffe066";

const GosvaraTemplate: React.FC<Props> = ({
  schoolId,
  classId,
  marksheetType,
  monthType,
  gosvaraData,
}) => {
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);
  const templateRef = useRef<HTMLDivElement>(null);
  
  // Commented out exam-sheet API call
  // const { useMonthlyGosvara } = useGosvaraSheet();
  const { useSchoolById } = useSchools();
  const { selectedSession, selectedClass } = useSessionClass();
  
  // Use passed data or fallback to API call (commented out)
  // const { data, isLoading, error } = useMonthlyGosvara(
  //   { schoolId, classId, marksheetType, monthType, sessionId: selectedSession?.id ?? 0 },
  //   !!schoolId && !!classId
  // );
  
  // Use the passed gosvaraData or show loading if not provided
  const data = gosvaraData;
  const isLoading = !gosvaraData;
  const error = null;
  
  const {
    data: schoolData,
    isLoading: schoolLoading,
    error: schoolError,
  } = useSchoolById(schoolId);

  // Fetch all subjects for the class
  const { data: classSubjects, isLoading: subjectsLoading } = useSubjectsByClassId(classId);
  // Fallback: some setups attach subjects to a base class name (e.g. "9th") rather than per-section classId.
  const { data: classSubjectsByName, isLoading: subjectsByNameLoading } = useSubjectsByClass(
    selectedClass?.name ?? "",
    false
  );

  const classNumber = useMemo(() => {
    const name = selectedClass?.name ?? "";
    const n = parseInt(String(name).match(/\d+/)?.[0] ?? "", 10);
    return Number.isFinite(n) ? n : null;
  }, [selectedClass?.name]);

  const { gradeHeaders, gradeRanges } = useMemo(() => {
    const plusGradeClasses = new Set([3, 4, 6, 7]);
    if (classNumber && plusGradeClasses.has(classNumber)) {
      return {
        gradeHeaders: ["A+", "A", "B+", "B", "C+", "C", "D", "E"],
        gradeRanges: [
          "(86 - 100)",
          "(76 - 85)",
          "(66 - 75)",
          "(56 - 65)",
          "(51 - 55)",
          "(46 - 50)",
          "(33 - 45)",
          "(0 - 32)",
        ],
      };
    }
    return { gradeHeaders: defaultGradeHeaders, gradeRanges: defaultGradeRanges };
  }, [classNumber]);

  // Merge backend gosvara data with all class subjects
  const mergedSubjectResults = useMemo(() => {
    if (!data?.subjectResults) return [];

    // Create a map of subjects from backend data (excluding TOTAL row)
    const backendSubjectsMap = new Map();
    let totalRow: any = null;

    data.subjectResults.forEach((subjectResult: any) => {
      if (subjectResult.subject === "TOTAL") {
        totalRow = subjectResult;
      } else {
        backendSubjectsMap.set(subjectResult.subject, subjectResult);
      }
    });

    // Get all subjects for the class.
    // Prefer subjects by classId; if missing for this section, fall back to subjects by class name (e.g. "9th").
    // Last resort: use subjects present in gosvara response so UI still renders.
    const allSubjects =
      (classSubjects && classSubjects.length > 0)
        ? classSubjects
        : (classSubjectsByName && classSubjectsByName.length > 0)
          ? classSubjectsByName
          : (data.subjectResults || [])
              .filter((sr: any) => sr?.subject && sr.subject !== "TOTAL")
              .map((sr: any) => ({ name: sr.subject }));
    
    // Create merged results - include all class subjects
    const mergedResults = allSubjects.map((subject: any) => {
      const subjectName = subject.name;
      
      // If backend has data for this subject, use it
      if (backendSubjectsMap.has(subjectName)) {
        return backendSubjectsMap.get(subjectName);
      }
      
      // Otherwise, create an empty row with 0 values
      const emptyGradeMap: { [key: string]: number } = {};
      gradeHeaders.forEach(grade => {
        emptyGradeMap[grade] = 0;
      });

      return {
        subject: subjectName,
        result: emptyGradeMap,
        registered: 0,
        appeared: 0,
        absent: 0,
        passed: 0,
        failed: 0,
        passPercentage: 0
      };
    });

    // Add TOTAL row at the end if it exists
    if (totalRow) {
      // Update totalRow to use overallGradeSummary for grade columns
      if (data?.overallGradeSummary) {
        totalRow.result = data.overallGradeSummary;
      }
      mergedResults.push(totalRow);
    }

    return mergedResults;
  }, [data?.subjectResults, classSubjects, classSubjectsByName, gradeHeaders]);

  // Function to download template as image
  const downloadAsImage = async () => {
    if (!templateRef.current) return;
    
    setIsDownloading(true);
    try {
      const canvas = await html2canvas(templateRef.current, {
        useCORS: true,
        allowTaint: true,
        background: '#ffffff',
        logging: false,
      });
      
      const link = document.createElement('a');
      link.download = `gosvara-template-${selectedClass?.name || 'class'}-${marksheetType}-${monthType}-${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
    } finally {
      setIsDownloading(false);
    }
  };

  // Don't block rendering on subjects APIs when gosvara already contains subject-wise rows.
  // For synthetic selections like "*-ALL" / "Class-ALL", classId/name might not map to a subjects endpoint.
  if (isLoading)
    return (
      <Box textAlign="center" p={4}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>
          {t("loading_data")}
        </Typography>
      </Box>
    );
  if (error)
    return (
      <Box textAlign="center" p={4}>
        <Typography color="error">{t("failed_to_load_data")}</Typography>
      </Box>
    );
  if (!data) return null;

  return (
    <Box sx={{ bgcolor: '#f8fafc', height: 'fit-content', pb: 0 ,mt:2, mx:1}}>
      <div ref={templateRef}>
        {/* Top Header */}
        <Card sx={{ 
          mb: 2, 
          borderRadius: 2, 
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          backgroundColor: '#3092e3'
        }}>
        <CardContent sx={{ p: 0.5, '&:last-child': { paddingBottom: 0 } }}>
          <Box sx={{ 
            display: "flex", 
            justifyContent: "flex-start", 
            alignItems: "center",
            textAlign: "left",
            pl: 2,
            py: 1
          }}>
            <Typography 
              variant="h4" 
              fontWeight="bold" 
              color="white"
              sx={{
                fontSize: {
                  xs: '1.5rem',    // Mobile: larger font
                  sm: '1.8rem',    // Small screens
                  md: '2.1rem',    // Medium screens
                  lg: '2.4rem',    // Large screens
                  xl: '2.7rem'     // Extra large screens
                },
                wordBreak: 'break-word',
                lineHeight: 1.1,
                maxWidth: '100%'
              }}
            >
              {(schoolData?.nameofSchool || "").toUpperCase()}
            </Typography>
          </Box>
        </CardContent>
      </Card>
      {/* Info Section - Modern Card Layout */}
      <Card sx={{ mb: 2, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
        <CardContent sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            {/* Left Section: School Details + Codes */}
            <Grid item xs={12} md={5}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                {/* First Column: District, Year, DISE */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Typography variant="body1" fontWeight="medium">{t("district")}:</Typography>
                    <Typography variant="body1" color="primary" fontWeight="bold">
                      {schoolData?.district || "INDORE"}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Typography variant="body1" fontWeight="medium">{t("year")}:</Typography>
                    <Typography variant="body1" color="primary" fontWeight="bold">
                      {selectedSession?.name || "2025-2026"}
                    </Typography>
                  </Box>
                  
                </Box>
                
                {/* Second Column: MPBSE, Medium */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Typography variant="body1" fontWeight="medium">{t("mpbse")}:</Typography>
                    <Typography variant="body1" color="primary" fontWeight="bold">
                      {schoolData?.schoolCode || "531062"}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Typography variant="body1" fontWeight="medium">{t("dise")}:</Typography>
                    <Typography variant="body1" color="primary" fontWeight="bold">
                      {schoolData?.diseCode || "23230119206"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Center Section: Gosvara Type and Month */}
            <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', gap: 0.5 }}>
                <Box sx={{ display: 'flex', gap: 1, whiteSpace: 'nowrap' }}>
                  <Typography variant="body1" fontWeight="medium">{t("goshwara_type")}:</Typography>
                  <Typography variant="body1" color="primary" fontWeight="bold">
                    {String(marksheetType ?? "").replace(/_/g, " ").toUpperCase()}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, whiteSpace: 'nowrap' }}>
                  <Typography variant="body1" fontWeight="medium">{t("month_type")}:</Typography>
                  <Typography variant="body1" color="primary" fontWeight="bold">
                    {monthType.slice(0, 3).toUpperCase()}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Right Section: Class + Section */}
            <Grid item xs={12} md={5}>
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-end', gap: 0.5, pr: 4 }}>
                <Box sx={{ display: 'flex', gap: 1, whiteSpace: 'nowrap' }}>
                  <Typography variant="body1" fontWeight="medium">{t("class")}:</Typography>
                  <Typography variant="body1" color="primary" fontWeight="bold">
                    {selectedClass?.name || "10th"}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, whiteSpace: 'nowrap' }}>
                  <Typography variant="body1" fontWeight="medium">{t("section")}:</Typography>
                  <Typography variant="body1" color="primary" fontWeight="bold">
                    {selectedClass?.section || "A"}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      <Card sx={{ borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', mb: 0, '&:last-child': { marginBottom: 0 } }}>
        <CardContent sx={{ p: 0, '&:last-child': { paddingBottom: 0 } }}>
          <TableContainer sx={{ 
            overflow: 'hidden'
          }}>
            <Table size="medium">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell
                    rowSpan={2}
                    sx={{ 
                      border: "1px solid #e0e0e0", 
                      fontWeight: "bold",
                      bgcolor: '#e3f2fd',
                      color: 'primary.main',
                      fontSize: '1.1rem'
                    }}
                  >
                    Subject
                  </TableCell>
                  {gradeHeaders.map((header, idx) => (
                    <TableCell
                      key={header}
                      align="center"
                      sx={{ 
                        border: "1px solid #e0e0e0", 
                        fontWeight: "bold",
                        bgcolor: '#e3f2fd',
                        color: 'primary.main',
                        fontSize: '1.1rem'
                      }}
                    >
                      {header}
                      <br />
                      {!!gradeRanges[idx] && (
                        <Typography variant="body2" sx={{ fontWeight: "normal", color: 'text.secondary' }}>
                          {gradeRanges[idx]}
                        </Typography>
                      )}
                    </TableCell>
                  ))}
                  <TableCell
                    align="center"
                    rowSpan={2}
                    sx={{ 
                      border: "1px solid #e0e0e0", 
                      fontWeight: "bold",
                      bgcolor: '#e3f2fd',
                      color: 'primary.main',
                      fontSize: '1.1rem'
                    }}
                  >
                    {t("registered")}
                  </TableCell>
                  <TableCell
                    align="center"
                    rowSpan={2}
                    sx={{ 
                      border: "1px solid #e0e0e0", 
                      fontWeight: "bold",
                      bgcolor: '#e3f2fd',
                      color: 'primary.main',
                      fontSize: '1.1rem'
                    }}
                  >
                    {t("appeared")}
                  </TableCell>
                  <TableCell
                    align="center"
                    rowSpan={2}
                    sx={{ 
                      border: "1px solid #e0e0e0", 
                      fontWeight: "bold",
                      bgcolor: '#e3f2fd',
                      color: 'primary.main',
                      fontSize: '1.1rem'
                    }}
                  >
                    {t("absent")}
                  </TableCell>
                  <TableCell
                    align="center"
                    rowSpan={2}
                    sx={{ 
                      border: "1px solid #e0e0e0", 
                      fontWeight: "bold",
                      bgcolor: '#e3f2fd',
                      color: 'primary.main',
                      fontSize: '1.1rem'
                    }}
                  >
                    {t("pass")}
                  </TableCell>
                  <TableCell
                    align="center"
                    rowSpan={2}
                    sx={{ 
                      border: "1px solid #e0e0e0", 
                      fontWeight: "bold",
                      bgcolor: '#e3f2fd',
                      color: 'primary.main',
                      fontSize: '1.1rem'
                    }}
                  >
                    {t("fail")}
                  </TableCell>
                  <TableCell
                    align="center"
                    rowSpan={2}
                    sx={{ 
                      border: "1px solid #e0e0e0", 
                      fontWeight: "bold",
                      bgcolor: '#e3f2fd',
                      color: 'primary.main',
                      fontSize: '1.1rem'
                    }}
                  >
                    {t("pass_percentage")}
                  </TableCell>
                </TableRow>
                <TableRow>{/* Grade ranges are already included above */}</TableRow>
              </TableHead>
              <TableBody>
                {mergedSubjectResults?.map((subject: any, idx: number) => {
                  // Check if this is the TOTAL row
                  const isTotalRow = subject.subject === "TOTAL";
                  
                  return (
                    <TableRow 
                      key={subject.subject || `subject-${idx}`}
                      sx={{ 
                        bgcolor: isTotalRow 
                          ? '#fff3cd' // Light yellow background for TOTAL row
                          : idx % 2 === 0 
                            ? '#fafafa' 
                            : 'white',
                        '&:hover': { bgcolor: isTotalRow ? '#fff3cd' : '#f0f0f0' },
                        borderTop: isTotalRow ? '3px solid #ff9800' : 'none' // Orange border for TOTAL row
                      }}
                    >
                      <TableCell
                        sx={{
                          border: "1px solid #e0e0e0",
                          color: isTotalRow
                            ? "#ff6f00" // Orange color for TOTAL
                            : subject.subject?.match(/^(\(\d+\))/)
                              ? "error.main"
                              : "text.primary",
                          fontWeight: isTotalRow 
                            ? "bold" 
                            : subject.subject?.match(/^(\(\d+\))/)
                              ? "bold"
                              : "medium",
                          bgcolor: isTotalRow
                            ? "#fff3cd"
                            : subject.subject?.match(/^(\(\d+\))/)
                              ? "#ffebee"
                              : "inherit",
                          fontSize: isTotalRow ? '1.1rem' : '1rem',
                          textTransform: isTotalRow ? 'uppercase' : 'none'
                        }}
                      >
                        {subject.subject || 'N/A'}
                      </TableCell>
                      {gradeHeaders.map((header) => (
                        <TableCell
                          key={header}
                          align="center"
                          sx={{ 
                            border: "1px solid #e0e0e0", 
                            fontWeight: "bold",
                            color: isTotalRow ? '#ff6f00' : '#000000',
                            fontSize: isTotalRow ? '1.1rem' : '1rem',
                            bgcolor: isTotalRow ? '#fff3cd' : 'inherit'
                          }}
                        >
                          {subject.result?.[header] ?? 0}
                        </TableCell>
                      ))}
                      <TableCell
                        align="center"
                        sx={{ 
                          border: "1px solid #e0e0e0", 
                          fontWeight: "bold",
                          color: isTotalRow ? '#ff6f00' : '#000000',
                          fontSize: isTotalRow ? '1.1rem' : '1rem',
                          bgcolor: isTotalRow ? '#fff3cd' : 'inherit'
                        }}
                      >
                        {subject.registered ?? 0}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ 
                          border: "1px solid #e0e0e0", 
                          fontWeight: "bold",
                          color: isTotalRow ? '#ff6f00' : '#000000',
                          fontSize: isTotalRow ? '1.1rem' : '1rem',
                          bgcolor: isTotalRow ? '#fff3cd' : 'inherit'
                        }}
                      >
                        {subject.appeared ?? 0}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ 
                          border: "1px solid #e0e0e0", 
                          fontWeight: "bold",
                          color: isTotalRow ? '#ff6f00' : '#000000',
                          fontSize: isTotalRow ? '1.1rem' : '1rem',
                          bgcolor: isTotalRow ? '#fff3cd' : 'inherit'
                        }}
                      >
                        {subject.absent ?? 0}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ 
                          border: "1px solid #e0e0e0", 
                          fontWeight: "bold",
                          color: isTotalRow ? '#ff6f00' : '#4caf50',
                          fontSize: isTotalRow ? '1.1rem' : '1rem',
                          bgcolor: isTotalRow ? '#fff3cd' : 'inherit'
                        }}
                      >
                        {subject.passed ?? 0}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ 
                          border: "1px solid #e0e0e0", 
                          fontWeight: "bold",
                          color: isTotalRow ? '#ff6f00' : '#f44336',
                          fontSize: isTotalRow ? '1.1rem' : '0.95rem',
                          bgcolor: isTotalRow ? '#fff3cd' : 'inherit'
                        }}
                      >
                        {subject.failed ?? 0}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ 
                          border: "1px solid #e0e0e0", 
                          fontWeight: "bold",
                          color: isTotalRow ? '#ff6f00' : '#000000',
                          fontSize: isTotalRow ? '1.1rem' : '0.95rem',
                          bgcolor: isTotalRow ? '#fff3cd' : 'inherit'
                        }}
                      >
                        {subject.passPercentage ?? 0}%
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
      </div>
      
      {/* Download Button - Outside template container */}
      <Box sx={{ textAlign: "center", mb: 2, mt: 2 }}>
        <Button
          variant="contained"
          startIcon={<Download />}
          onClick={downloadAsImage}
          disabled={isDownloading}
          sx={{
            backgroundColor: "#3092e3",
            "&:hover": {
              backgroundColor: "primary.main",
            },
          }}
        >
          {isDownloading ? t("generating_image") : t("download_as_image")}
        </Button>
      </Box>
    </Box>
  );
};

export default GosvaraTemplate;
